import { Card, Space, Popover, Button } from 'antd';
import React, { createContext, useState, useEffect, Fragment } from 'react';
import clsx from 'clsx';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import WidgetCustomize from 'components/Widget/WidgetCustomize';
import { useDashboardLayout } from 'store/redux/states/dashboard/dashboard.hooks';
import { getDateRangeByKey } from 'components/DateRangePicker/components/staticRanges';
import WidgetTitle from './components/WidgetTitle';
import WidgetConfiguration from './components/WidgetConfiguration';
import Configuration from './components/Configuration';
import Icon from '@mdi/react';
import { mdiDotsVertical } from '@mdi/js';
import Ninja from '@ninja';
import DateRangePicker from 'components/DateRangePicker';
import WidgetTypeSwitcher from './components/WidgetTypeSwitcher';
import NetworkDataSwitcher from 'components/switches/NetworkDataSwitcher';
import WidgetEmptyState from './components/WidgetEmptyState';

export const WidgetContext = createContext({});
const Widget = (props) => {
  const {
    // Widghet Params
    header = true,
    widget_key,
    title,
    index,
    dashboard_widget_key,
    tableWidget,
    icon,
    titleConfig,

    // filters Params
    disableLevelFilter,
    cardFilterComponent: CardFilterComponent = Fragment,
    connectToGetDataButton,
    widgetEmptyStateProps = {
      buttonText: 'Connect',
      redirectLink: '',
    },
    filterComponent,
    disableLimit,
    disableDateFilter,
    disableStatusFilter,
    showIntegrationFilters,
    integrationsSelect,
    disableAccountsFilter,
    showWoocommerceFilter,
    integrationType,
    default_filter,
    networkSwitch = true,
    adsFilter,
    adsetFilter,
    campaignFilter,
    showNetworkSwitcher,

    // Charts Params
    showCustomDesign = false,
    enableChart = false,
    changeMetricsDesign,
    network = 'all',
    configuration = false,
    showWidgetTypeSwitcher,
    disableFilter = false,
    customize = false,
    metricsList,
    showAllMetrics = false,
    data,
    load_chart = false,
    showDatePicker = false,
    changeAreaChartDesign,
    position,
    ...card
  } = props;

  const [open, setOpen] = useState(false);

  const closePopover = () => setOpen(false);
  const [runFilterTimestamp, setRunFilterTimestamp] = useState(new Date().getTime());
  const { config, updateWidget, isLoading } = useDashboardLayout();
  const { date_from, date_to, setDate, date_range_key } = useWidgetDate(widget_key);
  const [filters, setFilters] = useState(default_filter);
  const [defaultFilter, setDefaultFilter] = useState(default_filter);
  const [editableStr, setEditableStr] = useState(title);

  useEffect(() => {
    setFilters(default_filter);
    setDefaultFilter(default_filter);
  }, [default_filter]);

  const [chartConfig, setChartConfig] = useState(
    data?.config || {
      load_chart: data?.config?.load_chart || load_chart,
      metrics: {},
      showXAxis: false,
      showYAxis: false,
    }
  );

  useEffect(() => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      ...data?.config,
      load_chart: load_chart,
    }));
  }, [load_chart, data?.config]);

  useEffect(() => {
    if (data?.config?.metrics || metricsList) {
      const updatedMetrics = Object.fromEntries(
        Object.entries(data?.config?.metrics || metricsList)
          .filter(([, { show }]) =>
            !data?.config?.metrics && showAllMetrics ? true : show === true
          )
          .map(([key, { color, name, show, type, currency_symbol, currency }]) => [
            key,
            {
              type,
              color,
              name,
              show: showAllMetrics ? true : show,
              currency_symbol: currency_symbol,
              currency: currency,
            },
          ])
      );

      setChartConfig({
        load_chart: data?.config?.load_chart || load_chart,
        areaConfig: data?.config?.areaConfig || null,
        metrics: updatedMetrics,
        showXAxis: data?.config?.showXAxis || false,
        showYAxis: data?.config?.showYAxis || false,
        selected_metrics: data?.config?.selected_metrics,
      });
    }
  }, [metricsList, data]);

  useEffect(() => {
    const { date_from: key_date_from, date_to: key_date_to } = date_range_key
      ? getDateRangeByKey(date_range_key)
      : { date_from, date_to };

    setFilters((prevFilters) => ({
      ...prevFilters,
      date_range_key: date_range_key || null,
      date_from: key_date_from,
      date_to: key_date_to,
    }));
  }, [date_from, date_to, date_range_key]);

  useEffect(() => {
    const { date_from, date_to } = default_filter?.date_range_key
      ? getDateRangeByKey(default_filter.date_range_key)
      : { date_from: default_filter?.date_from, date_to: default_filter?.date_to };

    if (date_from && date_to) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        date_from,
        date_to,
      }));

      setDate({ date_from, date_to, date_range_key: default_filter?.date_range_key || null });
    }
  }, [default_filter]);

  const handleDelete = () => {
    const updatedConfig = config.filter((_, i) => i !== index);
    updateWidget(updatedConfig);
  };

  const runFilter = () => {
    setRunFilterTimestamp(new Date().getTime());
  };

  const changeLevel = (level) => {
    setFilters((prevFilters) => ({ ...prevFilters, level: level.target.value }));
    runFilter();
    props.onLevelChange?.(level.target.value);
  };

  const changeLimit = (value) => {
    setFilters((prevFilters) => ({ ...prevFilters, limit: value }));
    runFilter();
  };

  const changeWidgetType = (val) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      load_chart: val,
    }));
  };

  return (
    <WidgetContext.Provider
      value={{
        widget_key,
        title,
        titleConfig,
        filters,
        setFilters,
        runFilterTimestamp,
        runFilter,
        default_filter,
        chartConfig,
        setChartConfig,
        network,
        networkSwitch,
        metricsList,
        showAllMetrics,
        index,
        dashboard_widget_key,
        showCustomDesign,
        changeMetricsDesign,
        disableStatusFilter,
        showIntegrationFilters,
        integrationsSelect,
        showWoocommerceFilter,
        disableAccountsFilter,
        disableLevelFilter,
        changeLevel,
        disableDateFilter,
        adsFilter,
        adsetFilter,
        campaignFilter,
        showWidgetTypeSwitcher,
        filterComponent,
        changeAreaChartDesign,
        position,
        configuration,
        defaultFilter,
        setDefaultFilter,
        editableStr,
        setEditableStr,
        connectToGetDataButton,
        widgetEmptyStateProps,
        widget_filters: {
          ...filters,
        },
      }}
    >
      {configuration ? (
        <Configuration onChange={changeWidgetType} value={chartConfig?.load_chart}>
          {props.children}
        </Configuration>
      ) : (
        <Card
          title={header && <WidgetTitle icon={icon} title={title} titleConfig={titleConfig} />}
          extra={
            header && (
              <Space className='relative flex items-center w-full gap-2'>
                <div className='flex flex-wrap items-center gap-2'>
                  {showNetworkSwitcher && (
                    <NetworkDataSwitcher
                      onFilter={(value) =>
                        setFilters((prevConfig) => ({
                          ...prevConfig,
                          networks: value,
                        }))
                      }
                    />
                  )}
                  {(!disableDateFilter || showDatePicker) && (
                    <DateRangePicker
                      variant='outlined'
                      placement='bottomRight'
                      className='h-10'
                      style={{ maxWidth: 150 }}
                      formatter='yyyy-MM-dd'
                      changeGlobal={true}
                      placeholder={['Date from', 'Date to']}
                      date_key={widget_key}
                      globalDate={false}
                      value={[filters?.date_from, filters?.date_to]}
                      icon={false}
                      date_range_key={filters.date_range_key}
                    />
                  )}
                  {showWidgetTypeSwitcher && (
                    <WidgetTypeSwitcher
                      showLabel={false}
                      onChange={changeWidgetType}
                      value={chartConfig?.load_chart}
                    />
                  )}
                  {!disableFilter && (
                    <Popover
                      placement='bottom'
                      open={open}
                      trigger='click'
                      onOpenChange={(newOpen) => setOpen(newOpen)}
                      content={
                        <WidgetConfiguration
                          handleDelete={handleDelete}
                          integrationType={integrationType}
                          default_filter={default_filter}
                          index={index}
                          widget_key={widget_key}
                          disableDateFilter={disableDateFilter}
                          closePopover={closePopover}
                        />
                      }
                    >
                      <Button
                        type='text'
                        size='large'
                        icon={<Icon path={mdiDotsVertical} color={Ninja.colors.darkText} />}
                        onClick={() => setOpen(!open)}
                      />
                    </Popover>
                  )}
                  <CardFilterComponent />
                  {customize && (
                    <WidgetCustomize
                      disableLevelFilter={disableLevelFilter}
                      changeLevel={changeLevel}
                      filters={filters}
                      changeLimit={changeLimit}
                      defaultPerPage={filters?.limit || 10}
                      disableLimit={disableLimit}
                      handleDelete={handleDelete}
                      index={index}
                      editTitle={true}
                      deleteLoading={isLoading}
                      onEdit={() => setEditing(true)}
                    />
                  )}
                </div>
              </Space>
            )
          }
          className={clsx('widget-card w-full relative h-full', { 'table-widget': tableWidget })}
          {...card}
        >
          {connectToGetDataButton ? (
            <WidgetEmptyState>{props.children}</WidgetEmptyState>
          ) : (
            props.children
          )}
        </Card>
      )}
    </WidgetContext.Provider>
  );
};

export default Widget;
